import type { NodeObject, LinkObject } from 'react-force-graph-2d'
import type { GraphNode, GraphEdge } from '../api/knowledgeApi'
import type { GraphTheme } from './graphTheme'
import { isQuery, nodeColor, isDirectional, isOntology, relationFrom } from './graphModel'
import { nodeSize } from './graphLayout'

type Node = NodeObject<GraphNode>
const short = (text: string, length: number) =>
  text.length > length ? text.slice(0, length - 1) + '…' : text
function box(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath()
  ctx.roundRect(x, y, w, h, r)
}
export function paintPointer(n: Node, color: string, ctx: CanvasRenderingContext2D) {
  const { width, height } = nodeSize(n)
  ctx.fillStyle = color
  box(ctx, (n.x || 0) - width / 2 - 4, (n.y || 0) - height / 2 - 4, width + 8, height + 8, 9)
  ctx.fill()
}
export function drawNode(
  n: Node,
  ctx: CanvasRenderingContext2D,
  scale: number,
  options: {
    theme: GraphTheme
    active: boolean
    dim: boolean
    labels: boolean
    animate: boolean
    image: (n: GraphNode) => HTMLImageElement | undefined
  }
) {
  const { width: w, height: h } = nodeSize(n)
  const x = n.x || 0,
    y = n.y || 0,
    left = x - w / 2,
    top = y - h / 2
  const { theme } = options,
    color = nodeColor(n, theme)
  ctx.save()
  ctx.globalAlpha = options.dim ? 0.18 : 1
  ctx.shadowColor = theme.foreground + (options.active ? '1f' : '0b')
  ctx.shadowBlur = options.active ? 14 : 5
  ctx.shadowOffsetY = 3
  box(ctx, left, top, w, h, n.kind === 'artwork' ? 9 : 12)
  ctx.fillStyle = theme.card
  ctx.fill()
  ctx.shadowBlur = 0
  ctx.shadowOffsetY = 0
  ctx.strokeStyle = options.active || n.centre ? theme.gold : theme.border
  ctx.lineWidth = options.active || n.centre ? 1.8 : 0.8
  ctx.stroke()
  if (n.kind === 'artwork') {
    // A gallery mat preserves the entire composition, including portrait and landscape works.
    box(ctx, left + 6, top + 6, w - 12, 91, 5)
    ctx.fillStyle = theme.muted
    ctx.fill()
    const img = options.image(n)
    if (img?.complete && img.naturalWidth) {
      const ratio = Math.min((w - 24) / img.naturalWidth, 79 / img.naturalHeight)
      const iw = img.naturalWidth * ratio,
        ih = img.naturalHeight * ratio
      ctx.drawImage(img, x - iw / 2, top + 51.5 - ih / 2, iw, ih)
    } else {
      ctx.fillStyle = theme.mutedForeground
      ctx.textAlign = 'center'
      ctx.font = '9px Inter, system-ui, sans-serif'
      ctx.fillText('Artwork', x, top + 54)
    }
    ctx.textAlign = 'left'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = theme.foreground
    ctx.font = '500 12px Inter, system-ui, sans-serif'
    ctx.fillText(short(n.name, 16), left + 9, top + 111)
    ctx.fillStyle = theme.mutedForeground
    ctx.font = '9px Inter, system-ui, sans-serif'
    ctx.fillText(short(n.artistName || 'View artwork', 19), left + 9, top + 126)
  } else {
    ctx.fillStyle = color
    ctx.beginPath()
    ctx.arc(left + 13, y, 3.5, 0, Math.PI * 2)
    ctx.fill()
    ctx.textAlign = 'left'
    ctx.textBaseline = 'middle'
    ctx.font = `${n.centre ? '600' : '500'} 12px Inter, system-ui, sans-serif`
    ctx.fillStyle = theme.foreground
    ctx.fillText(short(n.name, n.centre || isQuery(n) ? 21 : 16), left + 23, y - 6)
    ctx.font = '8px Inter, system-ui, sans-serif'
    ctx.fillStyle = theme.mutedForeground
    ctx.fillText(
      isQuery(n) ? 'SEARCH QUERY' : (n.category || 'Concept').replace(/_/g, ' ').toUpperCase(),
      left + 23,
      y + 9
    )
  }
  // Readable pills carry their own labels; full titles and metadata live in the inspector.
  if (options.active && scale > 1.2 && options.labels) {
    ctx.strokeStyle = theme.gold + '60'
    ctx.lineWidth = 0.6
    box(ctx, left - 4, top - 4, w + 8, h + 8, 12)
    ctx.stroke()
  }
  if (options.animate && options.active) {
    ctx.strokeStyle = theme.gold + '80'
    ctx.lineWidth = 0.8
    ctx.setLineDash([9, 12])
    ctx.lineDashOffset = -performance.now() / 110
    box(ctx, left - 4, top - 4, w + 8, h + 8, 12)
    ctx.stroke()
  }
  ctx.restore()
}

function boundary(n: Node, toward: Node) {
  const dx = (toward.x || 0) - (n.x || 0),
    dy = (toward.y || 0) - (n.y || 0)
  const size = nodeSize(n)
  const t = Math.min(
    (size.width / 2 + 3) / (Math.abs(dx) || 0.001),
    (size.height / 2 + 3) / (Math.abs(dy) || 0.001)
  )
  return { x: (n.x || 0) + dx * t, y: (n.y || 0) + dy * t }
}
export function drawConnection(
  link: LinkObject<GraphNode, Omit<GraphEdge, 'source' | 'target'>>,
  ctx: CanvasRenderingContext2D,
  scale: number,
  options: {
    theme: GraphTheme
    active: boolean
    dim: boolean
    labels: boolean
  }
) {
  if (typeof link.source !== 'object' || typeof link.target !== 'object') return
  const a = boundary(link.source, link.target),
    b = boundary(link.target, link.source)
  const typed = isOntology(link.kind),
    directional = isDirectional(link.kind)
  const dx = b.x - a.x,
    dy = b.y - a.y,
    length = Math.hypot(dx, dy) || 1
  const offset = typed ? 14 : -5
  const c = {
    x: (a.x + b.x) / 2 - (dy / length) * offset,
    y: (a.y + b.y) / 2 + (dx / length) * offset,
  }
  const color = options.active
    ? options.theme.gold
    : typed
      ? options.theme.foreground
      : options.theme.mutedForeground
  ctx.save()
  ctx.globalAlpha = options.dim ? 0.08 : options.active || typed ? 0.8 : 0.23
  ctx.strokeStyle = color
  ctx.fillStyle = color
  ctx.lineWidth = (options.active ? 1.8 : typed ? 1.25 : 0.65) / Math.sqrt(scale)
  if (!typed && link.kind !== 'embodies') ctx.setLineDash([3 / scale, 4 / scale])
  ctx.beginPath()
  ctx.moveTo(a.x, a.y)
  ctx.quadraticCurveTo(c.x, c.y, b.x, b.y)
  ctx.stroke()
  if (directional) {
    const angle = Math.atan2(b.y - c.y, b.x - c.x),
      size = 5 / Math.sqrt(scale)
    ctx.setLineDash([])
    ctx.beginPath()
    ctx.moveTo(b.x, b.y)
    ctx.lineTo(b.x - size * Math.cos(angle - 0.45), b.y - size * Math.sin(angle - 0.45))
    ctx.lineTo(b.x - size * Math.cos(angle + 0.45), b.y - size * Math.sin(angle + 0.45))
    ctx.closePath()
    ctx.fill()
  }
  if (!options.dim && (options.active || (typed && options.labels && scale > 0.55))) {
    const text = relationFrom(link.kind, true)
    const font = Math.min(15, 9 / scale)
    ctx.font = `500 ${font}px Inter, system-ui, sans-serif`
    const w = ctx.measureText(text).width + 10
    const mx = (a.x + 2 * c.x + b.x) / 4,
      my = (a.y + 2 * c.y + b.y) / 4
    ctx.globalAlpha = 1
    ctx.setLineDash([])
    box(ctx, mx - w / 2, my - font, w, font * 1.8, 4)
    ctx.fillStyle = options.theme.card
    ctx.fill()
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = options.theme.foreground
    ctx.fillText(text, mx, my - font * 0.1)
  }
  ctx.restore()
}
